import { sql } from "drizzle-orm";
import type { WorkflowV2StepStatus } from "./workflow-kernel-v2-state.js";
import type { WorkflowV2DbLike } from "./workflow-kernel-v2-store.js";
import { executeWorkflowV2Query } from "./workflow-kernel-v2-db.js";
import { workflowV2Jsonb } from "./workflow-kernel-v2-json.js";

export type WorkflowV2StepRow = {
  id: number;
  run_id: string;
  step_key: string;
  step_index: number;
  status: WorkflowV2StepStatus;
  input_hash: string | null;
  output_json: unknown;
  error_json: unknown;
  error_code: string | null;
  attempt_count: number;
  max_attempts: number;
  runnable_at: string | Date;
  lease_owner: string | null;
  lease_epoch: number;
  lease_expires_at: string | Date | null;
  started_at: string | Date | null;
  heartbeat_at: string | Date | null;
  completed_at: string | Date | null;
  updated_at: string | Date;
};

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

export async function getWorkflowV2Step(
  db: WorkflowV2DbLike,
  runId: string,
  stepKey: string,
): Promise<WorkflowV2StepRow | null> {
  const result = await executeWorkflowV2Query(
    db,
    sql`
        SELECT *
        FROM _gencow_workflow_steps_v2
        WHERE run_id = ${runId}
          AND step_key = ${stepKey}
        LIMIT 1
    `,
  );
  return rowsFromResult<WorkflowV2StepRow>(result)[0] ?? null;
}

export async function claimWorkflowV2Step(options: {
  db: WorkflowV2DbLike;
  runId: string;
  stepKey: string;
  stepIndex: number;
  inputHash?: string | null;
  workerId: string;
  leaseEpoch: number;
  leaseMs: number;
}): Promise<WorkflowV2StepRow | null> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        INSERT INTO _gencow_workflow_steps_v2 (
            run_id,
            step_key,
            step_index,
            status,
            input_hash,
            attempt_count,
            lease_owner,
            lease_epoch,
            lease_expires_at,
            started_at,
            heartbeat_at,
            updated_at
        )
        VALUES (
            ${options.runId},
            ${options.stepKey},
            ${options.stepIndex},
            'running',
            ${options.inputHash ?? null},
            1,
            ${options.workerId},
            ${options.leaseEpoch},
            NOW() + (${options.leaseMs} * INTERVAL '1 millisecond'),
            NOW(),
            NOW(),
            NOW()
        )
        ON CONFLICT (run_id, step_key)
        DO UPDATE SET
            status = 'running',
            input_hash = COALESCE(_gencow_workflow_steps_v2.input_hash, EXCLUDED.input_hash),
            error_json = NULL,
            error_code = NULL,
            attempt_count = _gencow_workflow_steps_v2.attempt_count + 1,
            lease_owner = EXCLUDED.lease_owner,
            lease_epoch = EXCLUDED.lease_epoch,
            lease_expires_at = EXCLUDED.lease_expires_at,
            started_at = COALESCE(_gencow_workflow_steps_v2.started_at, NOW()),
            heartbeat_at = NOW(),
            completed_at = NULL,
            updated_at = NOW()
        WHERE _gencow_workflow_steps_v2.status <> 'completed'
          AND (
            _gencow_workflow_steps_v2.status <> 'running'
            OR (
              _gencow_workflow_steps_v2.lease_owner = EXCLUDED.lease_owner
              AND _gencow_workflow_steps_v2.lease_epoch = EXCLUDED.lease_epoch
            )
            OR _gencow_workflow_steps_v2.lease_expires_at IS NULL
            OR _gencow_workflow_steps_v2.lease_expires_at < NOW()
          )
        RETURNING *
    `,
  );
  return rowsFromResult<WorkflowV2StepRow>(result)[0] ?? null;
}

export async function completeWorkflowV2Step(options: {
  db: WorkflowV2DbLike;
  runId: string;
  stepKey: string;
  leaseOwner: string;
  leaseEpoch: number;
  output?: unknown;
}): Promise<boolean> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        UPDATE _gencow_workflow_steps_v2
        SET
            status = 'completed',
            output_json = ${workflowV2Jsonb(options.output ?? null)},
            error_json = NULL,
            error_code = NULL,
            lease_owner = NULL,
            lease_expires_at = NULL,
            heartbeat_at = NULL,
            completed_at = NOW(),
            updated_at = NOW()
        WHERE run_id = ${options.runId}
          AND step_key = ${options.stepKey}
          AND status = 'running'
          AND lease_owner = ${options.leaseOwner}
          AND lease_epoch = ${options.leaseEpoch}
        RETURNING id
    `,
  );
  return rowsFromResult<{ id: number }>(result).length === 1;
}

export async function failWorkflowV2Step(options: {
  db: WorkflowV2DbLike;
  runId: string;
  stepKey: string;
  leaseOwner: string;
  leaseEpoch: number;
  error: unknown;
  errorCode?: string | null;
}): Promise<boolean> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        UPDATE _gencow_workflow_steps_v2
        SET
            status = 'failed',
            error_json = ${workflowV2Jsonb(options.error)},
            error_code = ${options.errorCode ?? null},
            lease_owner = NULL,
            lease_expires_at = NULL,
            heartbeat_at = NULL,
            completed_at = NULL,
            updated_at = NOW()
        WHERE run_id = ${options.runId}
          AND step_key = ${options.stepKey}
          AND status = 'running'
          AND lease_owner = ${options.leaseOwner}
          AND lease_epoch = ${options.leaseEpoch}
        RETURNING id
    `,
  );
  return rowsFromResult<{ id: number }>(result).length === 1;
}
